import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { MapPin, Navigation, Loader2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { toast } from "sonner";

// Refactored components
import { TopBar } from "../components/user/TopBar";
import { NewStopBanner } from "../components/user/NewStopBanner";
import { RouteSummary } from "../components/user/RouteSummary";
import { StopItem } from "../components/user/StopItem";

// Custom hooks
import { useWebSocket } from "../hooks/useWebSocket";
import { useGeolocation } from "../hooks/useGeolocation";
import { useWakeLock } from "../hooks/useWakeLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icons
const createIcon = (color, number) => {
  return L.divIcon({
    className: "custom-div-icon",
    html: `<div class="custom-marker ${color === "green" ? "marker-completed" : "marker-pending"}">${number}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

const userIcon = L.divIcon({
  className: "custom-div-icon",
  html: `<div class="custom-marker marker-user pulse-animation"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg></div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

// Map updater component
function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

// Format duration
function formatDuration(seconds) {
  if (!seconds) return "N/A";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
}

export default function UserDashboard({ user, onLogout }) {
  const [postcodes, setPostcodes] = useState([]);
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(true);
  const [sheetHeight, setSheetHeight] = useState(280);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartHeight, setDragStartHeight] = useState(280);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [lastPostcodeCount, setLastPostcodeCount] = useState(0);
  const [newStopBanner, setNewStopBanner] = useState(null);
  const soundIntervalRef = useRef(null);

  const MIN_SHEET_HEIGHT = 80;
  const MAX_SHEET_HEIGHT = typeof window !== 'undefined' ? window.innerHeight * 0.95 : 800;

  const headers = useMemo(() => ({}), []);

  // Custom hooks
  const { 
    location: userLocation, 
    error: locationError, 
    loading: requestingLocation,
    requestLocation,
    setLocation: setUserLocation 
  } = useGeolocation({ watchPosition: true });

  // Keep screen on while driving (prevents GPS from stopping)
  useWakeLock();

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1108, audioContext.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.3);
      oscillator.frequency.setValueAtTime(1108, audioContext.currentTime + 0.45);
      
      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.6);
    } catch (e) {
      // Audio context creation can fail on some browsers/devices
      // This is non-critical so we silently ignore
    }
  }, []);

  const stopAllSounds = useCallback(() => {
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
  }, []);

  const startAlertSound = useCallback(() => {
    stopAllSounds();
    playNotificationSound();
    soundIntervalRef.current = setInterval(playNotificationSound, 3000);
  }, [playNotificationSound, stopAllSounds]);

  const showNewStopAlert = useCallback((count, message) => {
    setNewStopBanner({
      count: count,
      message: message || `${count} NEW STOP${count > 1 ? 'S' : ''} ADDED!`
    });
    
    if ('vibrate' in navigator) {
      navigator.vibrate([500, 200, 500, 200, 500]);
    }
    
    startAlertSound();
  }, [startAlertSound]);

  const dismissNewStopBanner = useCallback(() => {
    setNewStopBanner(null);
    stopAllSounds();
    localStorage.setItem('landslide_acknowledged_stops', lastPostcodeCount.toString());
  }, [stopAllSounds, lastPostcodeCount]);

  // Fetch route data
  const fetchRouteData = useCallback(async (location) => {
    try {
      const routeResponse = await axios.post(
        `${API}/route/calculate`,
        { lat: location.lat, lng: location.lng },
        { headers }
      );
      setRouteData(routeResponse.data);
      
      const allPostcodesResponse = await axios.get(`${API}/postcodes`, { headers });
      const allPostcodes = allPostcodesResponse.data;
      
      const incompleteWithEtas = routeResponse.data.postcodes || [];
      const completed = allPostcodes.filter(p => p.completed);
      
      setPostcodes([...incompleteWithEtas, ...completed]);
    } catch (err) {
      toast.error("Failed to calculate route");
    }
  }, [headers]);

  // WebSocket handlers
  const handleNewStop = useCallback(async () => {
    showNewStopAlert(1);
    if (userLocation) {
      await fetchRouteData(userLocation);
    }
    const response = await axios.get(`${API}/postcodes`, { headers });
    const newPostcodes = response.data.filter(p => !p.completed);
    setLastPostcodeCount(newPostcodes.length);
  }, [showNewStopAlert, userLocation, fetchRouteData, headers]);

  const handleRouteReordered = useCallback(async () => {
    toast.info('Route order updated by admin');
    if (userLocation) {
      await fetchRouteData(userLocation);
    }
  }, [userLocation, fetchRouteData]);

  const { wsConnected, sendLocationUpdate } = useWebSocket({
    apiUrl: process.env.REACT_APP_BACKEND_URL || '',
    onNewStop: handleNewStop,
    onRouteReordered: handleRouteReordered,
    enabled: !loading
  });

  // Send location updates via WebSocket
  useEffect(() => {
    if (userLocation && wsConnected) {
      sendLocationUpdate(userLocation);
      // Also send to server via HTTP as backup
      axios.post(`${API}/user/location`, userLocation, { headers }).catch(() => {});
    }
  }, [userLocation, wsConnected, sendLocationUpdate, headers]);

  // When user returns to app after backgrounding, push fresh location immediately
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestLocation()
          .then((loc) => {
            if (wsConnected) sendLocationUpdate(loc);
            axios.post(`${API}/user/location`, loc, { headers }).catch(() => {});
          })
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [requestLocation, wsConnected, sendLocationUpdate, headers]);

  // Initial load - runs once on mount
  useEffect(() => {
    const initializeMap = async () => {
      setLoading(true);
      try {
        const location = await requestLocation();
        await fetchRouteData(location);
      } catch (err) {
        const defaultLocation = { lat: 51.5074, lng: -0.1278 };
        setUserLocation(defaultLocation);
        await fetchRouteData(defaultLocation);
      } finally {
        setLoading(false);
      }
    };
    initializeMap();
  }, []);

  // Check for unacknowledged stops
  useEffect(() => {
    if (!loading) {
      const checkUnacknowledged = async () => {
        try {
          const response = await axios.get(`${API}/postcodes`, { headers });
          const pendingStops = response.data.filter(p => !p.completed);
          const currentCount = pendingStops.length;
          const acknowledgedCount = parseInt(localStorage.getItem('landslide_acknowledged_stops') || '0', 10);
          
          if (currentCount > acknowledgedCount) {
            showNewStopAlert(currentCount - acknowledgedCount, `${currentCount - acknowledgedCount} UNREAD STOP${currentCount - acknowledgedCount > 1 ? 'S' : ''}!`);
          }
          setLastPostcodeCount(currentCount);
        } catch (err) {
          // Non-critical: checking unacknowledged stops failed, will retry on next interval
        }
      };
      setTimeout(checkUnacknowledged, 1000);
    }
  }, [loading, headers, showNewStopAlert]);

  // Notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Cleanup sound on unmount
  useEffect(() => {
    return () => stopAllSounds();
  }, [stopAllSounds]);

  // Initialize lastPostcodeCount
  useEffect(() => {
    if (postcodes.length > 0 && lastPostcodeCount === 0) {
      setLastPostcodeCount(postcodes.filter(p => !p.completed).length);
    }
  }, [postcodes, lastPostcodeCount]);

  // Fallback polling
  useEffect(() => {
    if (!loading && !wsConnected) {
      const checkForNew = async () => {
        try {
          const response = await axios.get(`${API}/postcodes`, { headers });
          const newPostcodes = response.data.filter(p => !p.completed);
          
          if (lastPostcodeCount > 0 && newPostcodes.length > lastPostcodeCount) {
            showNewStopAlert(newPostcodes.length - lastPostcodeCount);
            if (userLocation) await fetchRouteData(userLocation);
          }
          setLastPostcodeCount(newPostcodes.length);
        } catch (err) {
          // Silently ignore polling errors
        }
      };
      const interval = setInterval(checkForNew, 5000);
      return () => clearInterval(interval);
    }
  }, [loading, wsConnected, lastPostcodeCount, showNewStopAlert, userLocation, fetchRouteData, headers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const location = await requestLocation();
      await fetchRouteData(location);
      toast.success("Route updated!");
    } catch (err) {
      if (userLocation) await fetchRouteData(userLocation);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleNotifications = async () => {
    if (!notificationsEnabled) {
      if ('Notification' in window && Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        if (permission === 'granted') {
          setNotificationsEnabled(true);
          toast.success('Notifications enabled');
        }
      }
    } else {
      setNotificationsEnabled(false);
      toast.success('Notifications disabled');
    }
  };

  const handleComplete = async (postcodeId) => {
    try {
      await axios.put(`${API}/postcodes/${postcodeId}/complete`, {}, { headers });
      setPostcodes(postcodes.map(p => p.id === postcodeId ? { ...p, completed: true } : p));
      toast.success("Stop marked as complete!");
    } catch (err) {
      toast.error("Failed to mark complete");
    }
  };

  const handleUncomplete = async (postcodeId) => {
    try {
      await axios.put(`${API}/postcodes/${postcodeId}/uncomplete`, {}, { headers });
      setPostcodes(postcodes.map(p => p.id === postcodeId ? { ...p, completed: false } : p));
      if (userLocation) await fetchRouteData(userLocation);
      toast.success("Stop restored!");
    } catch (err) {
      toast.error("Failed to restore stop");
    }
  };

  const [showMapsModal, setShowMapsModal] = useState(null);

  const handleOpenMaps = (postcode) => {
    // Show the maps selector modal
    setShowMapsModal(postcode);
  };

  const openInMapsApp = (postcode, app) => {
    const { lat, lng, name, postcode: pc } = postcode;
    const label = encodeURIComponent(name || pc);
    
    let url;
    switch (app) {
      case 'google':
        // Google Maps - works on all platforms
        url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${label}`;
        break;
      case 'apple':
        // Apple Maps - works on iOS
        url = `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`;
        break;
      case 'waze':
        // Waze - works if app is installed
        url = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
        break;
      default:
        // Generic geo URI as fallback
        url = `geo:${lat},${lng}?q=${lat},${lng}(${label})`;
    }
    
    window.open(url, '_blank');
    setShowMapsModal(null);
  };

  const handleEditNotes = (postcode) => {
    setEditingNotes(postcode.id);
    setNotesText(postcode.notes || "");
  };

  const handleSaveNotes = async (postcodeId) => {
    try {
      await axios.put(`${API}/postcodes/${postcodeId}/notes`, { notes: notesText }, { headers });
      setPostcodes(postcodes.map(p => p.id === postcodeId ? { ...p, notes: notesText } : p));
      setEditingNotes(null);
      setNotesText("");
      toast.success("Notes saved");
    } catch (err) {
      toast.error("Failed to save notes");
    }
  };

  const handleCancelNotes = () => {
    setEditingNotes(null);
    setNotesText("");
  };

  // Sheet drag handlers
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setDragStartY(clientY);
    setDragStartHeight(sheetHeight);
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const deltaY = dragStartY - clientY;
    const newHeight = Math.min(MAX_SHEET_HEIGHT, Math.max(MIN_SHEET_HEIGHT, dragStartHeight + deltaY));
    
    requestAnimationFrame(() => {
      setSheetHeight(newHeight);
      setSheetExpanded(newHeight > MIN_SHEET_HEIGHT + 20);
    });
  }, [isDragging, dragStartY, dragStartHeight, MIN_SHEET_HEIGHT, MAX_SHEET_HEIGHT]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setSheetExpanded(sheetHeight > MIN_SHEET_HEIGHT + 20);
  }, [sheetHeight, MIN_SHEET_HEIGHT]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const toggleSheet = () => {
    if (sheetExpanded) {
      setSheetHeight(MIN_SHEET_HEIGHT);
      setSheetExpanded(false);
    } else {
      setSheetHeight(280);
      setSheetExpanded(true);
    }
  };

  // Memoized values
  const routePolyline = useMemo(() => {
    if (routeData?.route_geometry) {
      return routeData.route_geometry.map(([lng, lat]) => [lat, lng]);
    }
    if (userLocation && postcodes.length > 0) {
      const points = [[userLocation.lat, userLocation.lng]];
      postcodes.forEach((pc) => {
        if (pc.lat && pc.lng) points.push([pc.lat, pc.lng]);
      });
      return points;
    }
    return [];
  }, [routeData, userLocation, postcodes]);

  const mapCenter = useMemo(() => {
    if (userLocation) return [userLocation.lat, userLocation.lng];
    return [51.5074, -0.1278];
  }, [userLocation]);

  const pendingCount = postcodes.filter((p) => !p.completed).length;

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-100">
        <Loader2 className="w-10 h-10 animate-spin text-slate-900 mb-4" />
        <p className="text-slate-600">Loading your route...</p>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col relative overflow-hidden" data-testid="user-dashboard">
      {/* Map Layer */}
      <div className="absolute inset-0 z-0">
        <MapContainer center={mapCenter} zoom={13} style={{ height: "100%", width: "100%" }} zoomControl={false}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater center={mapCenter} />

          {routePolyline.length > 1 && (
            <Polyline positions={routePolyline} color="#3B82F6" weight={4} opacity={0.8} dashArray="10, 10" />
          )}

          {userLocation && (
            <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
              <Popup>Your Location</Popup>
            </Marker>
          )}

          {postcodes.map((pc, index) => (
            <Marker key={pc.id} position={[pc.lat, pc.lng]} icon={createIcon(pc.completed ? "green" : "blue", index + 1)}>
              <Popup>
                <div className="text-center">
                  <p className="font-bold">{pc.name}</p>
                  <p className="text-sm text-gray-500">{pc.postcode}</p>
                  {!pc.completed && <p className="text-sm mt-1">ETA: {formatDuration(pc.cumulative_eta)}</p>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* UI Layer - Top elements only */}
      <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none">
        <div className="pointer-events-auto p-4">
          <TopBar
            notificationsEnabled={notificationsEnabled}
            notificationPermission={notificationPermission}
            onToggleNotifications={toggleNotifications}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            onLogout={onLogout}
          />

          {/* Location error banner */}
          {locationError && (
            <div className="mt-3 bg-amber-100/90 backdrop-blur-md text-amber-800 text-sm px-4 py-3 rounded-xl flex items-center justify-between gap-3">
              <span className="flex-1">{locationError}</span>
              <Button
                data-testid="retry-location-btn"
                size="sm"
                variant="outline"
                className="bg-white/80 hover:bg-white shrink-0"
                onClick={requestLocation}
                disabled={requestingLocation}
              >
                {requestingLocation ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Navigation className="w-4 h-4 mr-1" />Get Location</>
                )}
              </Button>
            </div>
          )}

          <NewStopBanner banner={newStopBanner} onDismiss={dismissNewStopBanner} />
          <RouteSummary routeData={routeData} />
        </div>
      </div>

      {/* Bottom Sheet */}
      <div className="fixed bottom-0 left-0 right-0 pointer-events-auto" style={{ zIndex: 100 }}>
        <motion.div
          className="bg-white rounded-t-3xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)]"
          style={{ height: sheetHeight }}
          animate={{ height: sheetHeight }}
          transition={{ type: "spring", damping: 30, stiffness: 400 }}
        >
          {/* Drag handle */}
          <div
            data-testid="sheet-drag-handle"
            className="w-full pt-4 pb-3 cursor-grab active:cursor-grabbing select-none touch-none bg-slate-50 rounded-t-3xl"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <div className="w-16 h-2 bg-slate-400 rounded-full mx-auto mb-3" />
            <button data-testid="sheet-toggle" className="w-full flex items-center justify-between px-6" onClick={toggleSheet}>
              <span className="font-bold text-slate-900 text-lg">{pendingCount} stop{pendingCount !== 1 ? 's' : ''} remaining</span>
              {sheetExpanded ? (
                <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              ) : (
                <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
              )}
            </button>
          </div>

          <AnimatePresence>
            {sheetExpanded && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="px-4 pb-6 overflow-hidden bg-white" style={{ height: sheetHeight - 100 }}>
                {postcodes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MapPin className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p>No postcodes to visit</p>
                    <p className="text-sm">Ask admin to add some postcodes</p>
                  </div>
                ) : (
                  <ScrollArea className="h-full custom-scrollbar" style={{ height: sheetHeight - 120 }}>
                    {(() => {
                      const pendingStops = postcodes.filter(p => !p.completed);
                      const completedStops = postcodes.filter(p => p.completed);
                      const orderedStops = [...pendingStops, ...completedStops];
                      
                      return orderedStops.map((pc, index) => {
                        const pendingIndex = pendingStops.findIndex(p => p.id === pc.id);
                        const displayNumber = pendingIndex >= 0 ? pendingIndex + 1 : null;
                        
                        return (
                          <StopItem
                            key={pc.id}
                            postcode={pc}
                            index={index}
                            displayNumber={displayNumber}
                            isFirst={pendingIndex === 0}
                            onComplete={handleComplete}
                            onUncomplete={handleUncomplete}
                            onOpenMaps={handleOpenMaps}
                            onEditNotes={handleEditNotes}
                            onSaveNotes={handleSaveNotes}
                            onCancelNotes={handleCancelNotes}
                            editingNotes={editingNotes}
                            notesText={notesText}
                            setNotesText={setNotesText}
                          />
                        );
                      });
                    })()}
                  </ScrollArea>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Maps App Selector Modal */}
      <AnimatePresence>
        {showMapsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-end justify-center z-[200] p-4"
            onClick={() => setShowMapsModal(null)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="bg-white rounded-t-3xl w-full max-w-md pb-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="w-12 h-1 bg-slate-300 rounded-full mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-900 text-center">
                  Navigate to {showMapsModal.name}
                </h3>
                <p className="text-sm text-slate-500 text-center mt-1">
                  Choose your preferred maps app
                </p>
              </div>
              
              <div className="p-4 space-y-3">
                {/* Google Maps */}
                <button
                  onClick={() => openInMapsApp(showMapsModal, 'google')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-7 h-7">
                      <path fill="#4285F4" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                      <circle fill="#fff" cx="12" cy="9" r="2.5"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-slate-900">Google Maps</p>
                    <p className="text-sm text-slate-500">Open in Google Maps</p>
                  </div>
                </button>

                {/* Apple Maps */}
                <button
                  onClick={() => openInMapsApp(showMapsModal, 'apple')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-7 h-7">
                      <path fill="#000" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-slate-900">Apple Maps</p>
                    <p className="text-sm text-slate-500">Open in Apple Maps</p>
                  </div>
                </button>

                {/* Waze */}
                <button
                  onClick={() => openInMapsApp(showMapsModal, 'waze')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-7 h-7">
                      <path fill="#33CCFF" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm4 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2-5c0 .55-.45 1-1 1H9c-.55 0-1-.45-1-1V9c0-.55.45-1 1-1h6c.55 0 1 .45 1 1v3z"/>
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-slate-900">Waze</p>
                    <p className="text-sm text-slate-500">Open in Waze</p>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setShowMapsModal(null)}
                className="mx-4 w-[calc(100%-2rem)] py-3 rounded-xl bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
